import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Chip,
  Grid,
  MenuItem,
  Switch,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Avatar,
  Tooltip,
  Zoom,
  Divider,
  Badge,
  Paper,
  Fab,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Business as BusinessIcon,
  FolderSpecial as ProjectIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Language as LanguageIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  WorkOutline as WorkOutlineIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { API } from '../services/api';
import { showSuccess, showError } from '../utils/notifications';
import { ConfirmDialog, ActionButton, FilterBar } from '../components';
import colors from '../theme/colors';

const MotionBox = motion(Box);
const MotionCard = motion(Card);
const MotionPaper = motion(Paper);

function Companies() {
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCompanyDialog, setOpenCompanyDialog] = useState(false);
  const [openProjectDialog, setOpenProjectDialog] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: '', id: null });
  const [editingCompany, setEditingCompany] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [industryFilter, setIndustryFilter] = useState('all');
  const [expandedCompany, setExpandedCompany] = useState(null);
  const [hoveredCompany, setHoveredCompany] = useState(null);
  const scrollPositionRef = React.useRef(0);
  
  const [companyFormData, setCompanyFormData] = useState({
    company_name: '',
    company_code: '',
    logo_url: '',
    industry: '',
    website: '',
    status: 1,
  });

  const [projectFormData, setProjectFormData] = useState({
    project_name: '',
    project_code: '',
    company_id: '',
    project_type: '',
    description: '',
    location: '',
    start_date: '',
    end_date: '',
    status: 1,
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [companiesRes, projectsRes] = await Promise.all([
        API.companies.list(),
        API.projects.list(),
      ]);
      setCompanies(companiesRes?.data || []);
      setProjects(projectsRes?.data || []);
    } catch (err) {
      showError('Failed to load data');
      console.error('Data fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Company Dialog Handlers
  const handleOpenCompanyDialog = (company = null) => {
    if (company) {
      setEditingCompany(company);
      setCompanyFormData({
        company_name: company.company_name || '',
        company_code: company.company_code || '',
        logo_url: company.logo_url || '',
        industry: company.industry || '',
        website: company.website || '',
        status: company.status !== undefined ? company.status : 1,
      });
    } else {
      setEditingCompany(null);
      setCompanyFormData({
        company_name: '',
        company_code: '',
        logo_url: '',
        industry: '',
        website: '',
        status: 1,
      });
    }
    setOpenCompanyDialog(true);
  };

  const handleCloseCompanyDialog = () => {
    setOpenCompanyDialog(false);
    setEditingCompany(null);
    setCompanyFormData({
      company_name: '',
      company_code: '',
      logo_url: '',
      industry: '',
      website: '',
      status: 1,
    });
  };

  const handleSubmitCompany = async () => {
    try {
      scrollPositionRef.current = window.pageYOffset;
      if (editingCompany) {
        const { company_code, ...updateData } = companyFormData;
        await API.companies.update(editingCompany.company_id, updateData);
        showSuccess('Company updated successfully!');
      } else {
        const { status, ...createData } = companyFormData;
        await API.companies.create(createData);
        showSuccess('Company created successfully!');
      }
      handleCloseCompanyDialog();
      await fetchData();
      setTimeout(() => window.scrollTo(0, scrollPositionRef.current), 100);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
        (editingCompany ? 'Failed to update company' : 'Failed to create company');
      showError(errorMessage);
      console.error('Company submit error:', err);
    }
  };

  // Project Dialog Handlers
  const handleOpenProjectDialog = (company, project = null) => {
    setSelectedCompany(company);
    if (project) {
      setEditingProject(project);
      setProjectFormData({
        project_name: project.project_name || '',
        project_code: project.project_code || '',
        company_id: company.company_id,
        project_type: project.project_type || '',
        description: project.description || '',
        location: project.location || '',
        start_date: project.start_date || '',
        end_date: project.end_date || '',
        status: project.status !== undefined ? project.status : 1,
      });
    } else {
      setEditingProject(null);
      setProjectFormData({
        project_name: '',
        project_code: '',
        company_id: company.company_id,
        project_type: '',
        description: '',
        location: '',
        start_date: '',
        end_date: '',
        status: 1,
      });
    }
    setOpenProjectDialog(true);
  };

  const handleCloseProjectDialog = () => {
    setOpenProjectDialog(false);
    setEditingProject(null);
    setSelectedCompany(null);
    setProjectFormData({
      project_name: '',
      project_code: '',
      company_id: '',
      project_type: '',
      description: '',
      location: '',
      start_date: '',
      end_date: '',
      status: 1,
    });
  };

  const handleSubmitProject = async () => {
    try {
      scrollPositionRef.current = window.pageYOffset;
      if (editingProject) {
        // FIX: Remove project_code and company_id from update payload
        const { project_code, company_id, status, ...updateData } = projectFormData;
        await API.projects.update(editingProject.project_id, updateData);
        showSuccess('Project updated successfully!');
      } else {
        const { status, ...createData } = projectFormData;
        await API.projects.create(createData);
        showSuccess('Project created successfully!');
      }
      handleCloseProjectDialog();
      await fetchData();
      setTimeout(() => window.scrollTo(0, scrollPositionRef.current), 100);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
        (editingProject ? 'Failed to update project' : 'Failed to create project');
      showError(errorMessage);
      console.error('Project submit error:', err);
    }
  };

  // Delete Handlers
  const handleDeleteCompany = async () => {
    try {
      scrollPositionRef.current = window.pageYOffset;
      await API.companies.delete(confirmDialog.id);
      showSuccess('Company deleted successfully!');
      await fetchData();
      setConfirmDialog({ open: false, type: '', id: null });
      setTimeout(() => window.scrollTo(0, scrollPositionRef.current), 100);
    } catch (err) {
      showError('Failed to delete company');
      console.error('Company delete error:', err);
    }
  };

  const handleDeleteProject = async () => {
    try {
      scrollPositionRef.current = window.pageYOffset;
      await API.projects.delete(confirmDialog.id);
      showSuccess('Project deleted successfully!');
      await fetchData();
      setConfirmDialog({ open: false, type: '', id: null });
      setTimeout(() => window.scrollTo(0, scrollPositionRef.current), 100);
    } catch (err) {
      showError('Failed to delete project');
      console.error('Project delete error:', err);
    }
  };

  // Status Toggle Handlers
  const handleCompanyStatusToggle = async (companyId, currentStatus) => {
    try {
      scrollPositionRef.current = window.pageYOffset;
      const newStatus = currentStatus === 1 ? 0 : 1;
      await API.companies.update(companyId, { status: newStatus });
      showSuccess(`Company ${newStatus === 1 ? 'activated' : 'deactivated'} successfully!`);
      await fetchData();
      setTimeout(() => window.scrollTo(0, scrollPositionRef.current), 100);
    } catch (err) {
      showError('Failed to update company status');
      console.error('Company status update error:', err);
    }
  };

  const handleProjectStatusToggle = async (projectId, currentStatus) => {
    try {
      scrollPositionRef.current = window.pageYOffset;
      const newStatus = currentStatus === 1 ? 0 : 1;
      await API.projects.update(projectId, { status: newStatus });
      showSuccess(`Project ${newStatus === 1 ? 'activated' : 'deactivated'} successfully!`);
      await fetchData();
      setTimeout(() => window.scrollTo(0, scrollPositionRef.current), 100);
    } catch (err) {
      showError('Failed to update project status');
      console.error('Project status update error:', err);
    }
  };

  // Filter Companies
  const filteredCompanies = companies.filter(company => {
    const matchesSearch = company.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.company_code?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || company.status === parseInt(statusFilter);
    const matchesIndustry = industryFilter === 'all' || company.industry === industryFilter;
    
    return matchesSearch && matchesStatus && matchesIndustry;
  });

  // Get Projects for a Company
  const getCompanyProjects = (companyId) => {
    return projects.filter(project => project.company_id === companyId);
  };

  const uniqueIndustries = [...new Set(companies.map(c => c.industry).filter(Boolean))];

  const filterFields = [
    {
      label: 'Status',
      value: statusFilter,
      onChange: setStatusFilter,
      options: [
        { value: 'all', label: 'All Status' },
        { value: '1', label: 'Active' },
        { value: '0', label: 'Inactive' },
      ],
    },
    {
      label: 'Industry',
      value: industryFilter,
      onChange: setIndustryFilter,
      options: [
        { value: 'all', label: 'All Industries' },
        ...uniqueIndustries.map(industry => ({ value: industry, label: industry })),
      ],
    },
  ];

  const getStatusColor = (status) => status === 1 ? 'success' : 'error';
  const getStatusLabel = (status) => status === 1 ? 'Active' : 'Inactive';

  const handleAccordionChange = (companyId) => (event, isExpanded) => {
    setExpandedCompany(isExpanded ? companyId : null);
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh',
        flexDirection: 'column',
        gap: 2,
      }}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        >
          <BusinessIcon sx={{ fontSize: 60, color: '#6366F1' }} />
        </motion.div>
        <Typography variant="h6" color="text.secondary">Loading companies...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4, position: 'relative' }}>
      {/* Stunning Hero Header */}
      <MotionBox
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        sx={{ 
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #d946ef 100%)',
          borderRadius: 3,
          p: { xs: 2, md: 2.5 },
          mb: 3,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(99, 102, 241, 0.3)',
        }}
      >
        {/* Animated Background Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            filter: 'blur(60px)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -50,
            left: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            filter: 'blur(40px)',
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                <motion.div
                  animate={{ rotate: [0, 5, -5, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                  <BusinessIcon sx={{ fontSize: 28 }} />
                </motion.div>
                <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: -0.5 }}>
                  Companies & Projects
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.95, fontWeight: 400, maxWidth: 600, mb: 1.5 }}>
                Centralized hub for managing organizations and projects
              </Typography>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Box sx={{ cursor: 'default' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{companies.length}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>Companies</Typography>
                  </Box>
                </motion.div>
                <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.3)' }} />
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Box sx={{ cursor: 'default' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{projects.length}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>Projects</Typography>
                  </Box>
                </motion.div>
              </Box>
            </Box>
            <Box>
              <Tooltip title="Create New Company" arrow>
                <Fab
                  color="inherit"
                  size="medium"
                  onClick={() => handleOpenCompanyDialog()}
                  sx={{
                    backgroundColor: 'white',
                    color: '#6366f1',
                    boxShadow: '0 6px 24px rgba(99, 102, 241, 0.25)',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      transform: 'scale(1.1) rotate(90deg)',
                      boxShadow: '0 8px 32px rgba(99, 102, 241, 0.35)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <AddIcon sx={{ fontSize: 24 }} />
                </Fab>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </MotionBox>

      {/* Filter Bar */}
      <Box sx={{ mb: 4 }}>
        <FilterBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search companies by name or code..."
          filterFields={filterFields}
        />
      </Box>

      {/* Companies Grid */}
      <Grid container spacing={3}>
        <AnimatePresence>
          {filteredCompanies.map((company, index) => {
            const companyProjects = getCompanyProjects(company.company_id);
            const isExpanded = expandedCompany === company.company_id;
            const isHovered = hoveredCompany === company.company_id;

            return (
              <Grid item xs={12} key={company.company_id}>
                <MotionCard
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  onMouseEnter={() => setHoveredCompany(company.company_id)}
                  onMouseLeave={() => setHoveredCompany(null)}
                  sx={{
                    borderRadius: 4,
                    overflow: 'hidden',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    background: isHovered 
                      ? 'linear-gradient(135deg, #f0f4ff 0%, #faf5ff 100%)' 
                      : 'white',
                    border: `2px solid ${isHovered ? '#6366f1' : 'rgba(0,0,0,0.08)'}`,
                    boxShadow: isHovered 
                      ? '0 20px 60px rgba(99, 102, 241, 0.25), 0 0 0 4px rgba(99, 102, 241, 0.1)' 
                      : '0 2px 12px rgba(0,0,0,0.06)',
                    transform: isHovered ? 'translateY(-8px)' : 'translateY(0)',
                  }}
                >
                  <Accordion
                    expanded={isExpanded}
                    onChange={handleAccordionChange(company.company_id)}
                    sx={{
                      boxShadow: 'none',
                      '&:before': { display: 'none' },
                      backgroundColor: 'transparent',
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <Tooltip title={isExpanded ? "Click to collapse" : "Click to view projects"} arrow>
                          <motion.div
                            animate={isExpanded ? {} : { scale: [1, 1.05, 1] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                          >
                            <Box
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 3,
                                background: isExpanded 
                                  ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                                  : 'linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.3s ease',
                                boxShadow: '0 4px 20px rgba(99, 102, 241, 0.3)',
                                '&:hover': {
                                  transform: 'scale(1.1) rotate(180deg)',
                                  boxShadow: '0 6px 30px rgba(99, 102, 241, 0.5)',
                                },
                              }}
                            >
                              <ExpandMoreIcon sx={{ color: 'white', fontSize: 28 }} />
                            </Box>
                          </motion.div>
                        </Tooltip>
                      }
                      sx={{
                        '& .MuiAccordionSummary-content': {
                          my: 3,
                        },
                        px: 3,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2, gap: 3 }}>
                        {/* Company Logo */}
                        <Box
                          sx={{
                            width: 90,
                            height: 90,
                            borderRadius: 2,
                            background: company.logo_url ? 'white' : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '2.2rem',
                            fontWeight: 900,
                            color: 'white',
                            boxShadow: '0 8px 24px rgba(99, 102, 241, 0.3)',
                            border: '4px solid white',
                            overflow: 'hidden',
                          }}
                        >
                          {company.logo_url ? (
                            <img src={company.logo_url} alt={company.company_name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                          ) : (
                            company.company_name.charAt(0).toUpperCase()
                          )}
                        </Box>

                        {/* Company Info */}
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a1a1a' }}>
                              {company.company_name}
                            </Typography>
                            <motion.div
                              whileHover={{ scale: 1.1 }}
                              transition={{ type: "spring", stiffness: 400 }}
                            >
                              <Chip 
                                label={company.company_code} 
                                size="medium"
                                sx={{ 
                                  fontWeight: 700,
                                  fontSize: '0.85rem',
                                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                                  color: 'white',
                                  height: 32,
                                  px: 1,
                                  boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
                                }}
                              />
                            </motion.div>
                            <Chip 
                              icon={company.status === 1 ? <CheckCircleIcon /> : <CancelIcon />}
                              label={getStatusLabel(company.status)}
                              color={getStatusColor(company.status)}
                              size="medium"
                              sx={{ 
                                fontWeight: 700,
                                height: 32,
                              }}
                            />
                            <motion.div
                              animate={{ y: [0, -2, 0] }}
                              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                            >
                              <Badge 
                                badgeContent={companyProjects.length} 
                                sx={{
                                  '& .MuiBadge-badge': {
                                    background: 'linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)',
                                    color: 'white',
                                    fontWeight: 800,
                                    fontSize: '0.85rem',
                                    minWidth: 28,
                                    height: 28,
                                    borderRadius: 14,
                                    boxShadow: '0 2px 8px rgba(236, 72, 153, 0.4)',
                                  }
                                }}
                              >
                                <Chip 
                                  icon={<ProjectIcon />}
                                  label="Projects"
                                  size="medium"
                                  sx={{ 
                                    backgroundColor: '#f5f5f5',
                                    fontWeight: 600,
                                    height: 32,
                                  }}
                                />
                              </Badge>
                            </motion.div>
                          </Box>

                          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                            {company.industry && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AssessmentIcon sx={{ fontSize: 20, color: '#6366f1' }} />
                                <Typography variant="body1" sx={{ fontWeight: 600, color: '#666' }}>
                                  {company.industry}
                                </Typography>
                              </Box>
                            )}
                            {company.website && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LanguageIcon sx={{ fontSize: 20, color: '#6366f1' }} />
                                <Typography 
                                  variant="body1" 
                                  component="a"
                                  href={company.website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ 
                                    color: '#6366f1', 
                                    textDecoration: 'none',
                                    fontWeight: 600,
                                    '&:hover': { 
                                      textDecoration: 'underline',
                                      color: '#8b5cf6',
                                    }
                                  }}
                                >
                                  {company.website.replace(/^https?:\/\//, '')}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>

                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                          <Tooltip title={company.status === 1 ? "Active - Click to deactivate" : "Inactive - Click to activate"} arrow>
                            <Box>
                              <Switch
                                checked={company.status === 1}
                                onChange={() => handleCompanyStatusToggle(company.company_id, company.status)}
                                sx={{
                                  '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: '#6366f1',
                                  },
                                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: '#6366f1',
                                  },
                                }}
                              />
                            </Box>
                          </Tooltip>
                          <Tooltip title="Edit Company" arrow>
                            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenCompanyDialog(company);
                                }}
                                sx={{
                                  width: 44,
                                  height: 44,
                                  backgroundColor: '#6366f115',
                                  color: '#6366f1',
                                  '&:hover': { 
                                    backgroundColor: '#6366f1',
                                    color: 'white',
                                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)',
                                  },
                                  transition: 'all 0.3s ease',
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                            </motion.div>
                          </Tooltip>
                          <Tooltip title="Delete Company" arrow>
                            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setConfirmDialog({ open: true, type: 'company', id: company.company_id });
                                }}
                                sx={{
                                  width: 44,
                                  height: 44,
                                  backgroundColor: '#ef444415',
                                  color: '#ef4444',
                                  '&:hover': { 
                                    backgroundColor: '#ef4444',
                                    color: 'white',
                                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
                                  },
                                  transition: 'all 0.3s ease',
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </motion.div>
                          </Tooltip>
                        </Box>
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails sx={{ px: 4, pb: 4, pt: 0 }}>
                      <Divider sx={{ mb: 4, borderColor: 'rgba(99, 102, 241, 0.15)', borderWidth: 2 }} />
                      
                      {/* Projects Section */}
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <motion.div
                              animate={{ rotate: [0, 5, -5, 0] }}
                              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                            >
                              <ProjectIcon sx={{ fontSize: 32, color: '#6366f1' }} />
                            </motion.div>
                            <Box>
                              <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a1a1a' }}>
                                Projects
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {companyProjects.length} project{companyProjects.length !== 1 ? 's' : ''} under this company
                              </Typography>
                            </Box>
                          </Box>
                          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                            <ActionButton
                              startIcon={<AddIcon />}
                              onClick={() => handleOpenProjectDialog(company)}
                              sx={{
                                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                                color: 'white',
                                px: 3,
                                py: 1.5,
                                borderRadius: 3,
                                fontWeight: 700,
                                fontSize: '1rem',
                                boxShadow: '0 4px 20px rgba(99, 102, 241, 0.3)',
                                '&:hover': {
                                  boxShadow: '0 6px 30px rgba(99, 102, 241, 0.45)',
                                  transform: 'translateY(-2px)',
                                },
                                transition: 'all 0.3s ease',
                              }}
                            >
                              New Project
                            </ActionButton>
                          </motion.div>
                        </Box>

                        {companyProjects.length === 0 ? (
                          <MotionPaper
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3 }}
                            sx={{ 
                              textAlign: 'center', 
                              py: 8,
                              background: 'linear-gradient(135deg, #f8f9ff 0%, #fef5ff 100%)',
                              borderRadius: 4,
                              border: '3px dashed rgba(99, 102, 241, 0.25)',
                            }}
                          >
                            <motion.div
                              animate={{ y: [0, -10, 0] }}
                              transition={{ duration: 2, repeat: Infinity }}
                            >
                              <WorkOutlineIcon sx={{ fontSize: 80, color: 'rgba(99, 102, 241, 0.25)', mb: 2 }} />
                            </motion.div>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#6366f1', mb: 1 }}>
                              No projects yet
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
                              Get started by creating your first project for {company.company_name}
                            </Typography>
                            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                              <ActionButton
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenProjectDialog(company)}
                                variant="contained"
                                sx={{
                                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                                  color: 'white',
                                  px: 4,
                                  py: 1.5,
                                  borderRadius: 3,
                                  fontWeight: 700,
                                  fontSize: '1.1rem',
                                  boxShadow: '0 4px 16px rgba(99, 102, 241, 0.3)',
                                }}
                              >
                                Create First Project
                              </ActionButton>
                            </motion.div>
                          </MotionPaper>
                        ) : (
                          <Stack spacing={2}>
                            <AnimatePresence>
                              {companyProjects.map((project, pIndex) => (
                                <motion.div
                                  key={project.project_id}
                                  initial={{ opacity: 0, x: -30 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  exit={{ opacity: 0, x: 30 }}
                                  transition={{ 
                                    duration: 0.4, 
                                    delay: isExpanded ? pIndex * 0.08 : 0,
                                    ease: [0.4, 0, 0.2, 1]
                                  }}
                                >
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      p: 3,
                                      borderRadius: 3,
                                      background: 'white',
                                      border: '2px solid rgba(0,0,0,0.06)',
                                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                      cursor: 'pointer',
                                      position: 'relative',
                                      overflow: 'hidden',
                                      '&:hover': {
                                        borderColor: '#6366f1',
                                        boxShadow: '0 8px 32px rgba(99, 102, 241, 0.2), 0 0 0 2px rgba(99, 102, 241, 0.1)',
                                        transform: 'translateX(8px)',
                                        background: 'linear-gradient(90deg, #ffffff 0%, #f0f4ff 100%)',
                                      },
                                      '&:hover::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: '-100%',
                                        width: '100%',
                                        height: '100%',
                                        background: 'linear-gradient(90deg, transparent, rgba(99, 102, 241, 0.1), transparent)',
                                        animation: 'shimmer 0.8s',
                                      },
                                      '@keyframes shimmer': {
                                        '0%': { left: '-100%' },
                                        '100%': { left: '100%' },
                                      },
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                      {/* Project Icon/Number */}
                                      <motion.div
                                        animate={{ rotate: [0, 5, -5, 0] }}
                                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                                      >
                                        <Box
                                          sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: 2.5,
                                            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            boxShadow: '0 4px 16px rgba(99, 102, 241, 0.3)',
                                          }}
                                        >
                                          <Typography variant="h5" sx={{ color: 'white', fontWeight: 800 }}>
                                            {pIndex + 1}
                                          </Typography>
                                        </Box>
                                      </motion.div>

                                      {/* Project Details */}
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                                          <Typography variant="h6" sx={{ fontWeight: 800, color: '#1a1a1a' }}>
                                            {project.project_name}
                                          </Typography>
                                          <Chip 
                                            label={project.project_code} 
                                            size="small" 
                                            sx={{ 
                                              fontSize: '0.8rem',
                                              fontWeight: 700,
                                              background: 'linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)',
                                              color: 'white',
                                              height: 28,
                                              boxShadow: '0 2px 8px rgba(236, 72, 153, 0.3)',
                                            }}
                                          />
                                          <Chip 
                                            icon={project.status === 1 ? <CheckCircleIcon /> : <CancelIcon />}
                                            label={getStatusLabel(project.status)}
                                            color={getStatusColor(project.status)}
                                            size="small"
                                            sx={{ fontWeight: 700, height: 28 }}
                                          />
                                        </Box>

                                        {project.description && (
                                          <Typography 
                                            variant="body2" 
                                            sx={{ 
                                              color: '#666',
                                              mb: 1.5,
                                              display: '-webkit-box',
                                              WebkitLineClamp: 1,
                                              WebkitBoxOrient: 'vertical',
                                              overflow: 'hidden',
                                            }}
                                          >
                                            {project.description}
                                          </Typography>
                                        )}

                                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                          {project.project_type && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <TrendingUpIcon sx={{ fontSize: 18, color: '#6366f1' }} />
                                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#666' }}>
                                                {project.project_type}
                                              </Typography>
                                            </Box>
                                          )}
                                          {project.location && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <LocationIcon sx={{ fontSize: 18, color: '#6366f1' }} />
                                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#666' }}>
                                                {project.location}
                                              </Typography>
                                            </Box>
                                          )}
                                          {project.start_date && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <CalendarIcon sx={{ fontSize: 18, color: '#6366f1' }} />
                                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#666' }}>
                                                {new Date(project.start_date).toLocaleDateString()}
                                                {project.end_date && ` - ${new Date(project.end_date).toLocaleDateString()}`}
                                              </Typography>
                                            </Box>
                                          )}
                                        </Box>
                                      </Box>

                                      {/* Actions */}
                                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
                                        <Switch
                                          checked={project.status === 1}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            handleProjectStatusToggle(project.project_id, project.status);
                                          }}
                                          size="small"
                                          sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                              color: '#10b981',
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                              backgroundColor: '#10b981',
                                            },
                                          }}
                                        />
                                        <Tooltip title="Edit Project" arrow>
                                          <motion.div whileHover={{ scale: 1.1, rotate: 5 }} whileTap={{ scale: 0.95 }}>
                                            <IconButton
                                              size="medium"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenProjectDialog(company, project);
                                              }}
                                              sx={{ 
                                                width: 40,
                                                height: 40,
                                                backgroundColor: '#6366f110',
                                                color: '#6366f1',
                                                '&:hover': {
                                                  backgroundColor: '#6366f1',
                                                  color: 'white',
                                                  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)',
                                                },
                                                transition: 'all 0.2s ease',
                                              }}
                                            >
                                              <EditIcon fontSize="small" />
                                            </IconButton>
                                          </motion.div>
                                        </Tooltip>
                                        <Tooltip title="Delete Project" arrow>
                                          <motion.div whileHover={{ scale: 1.1, rotate: -5 }} whileTap={{ scale: 0.95 }}>
                                            <IconButton
                                              size="medium"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setConfirmDialog({ open: true, type: 'project', id: project.project_id });
                                              }}
                                              sx={{ 
                                                width: 40,
                                                height: 40,
                                                backgroundColor: '#ef444410',
                                                color: '#ef4444',
                                                '&:hover': {
                                                  backgroundColor: '#ef4444',
                                                  color: 'white',
                                                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
                                                },
                                                transition: 'all 0.2s ease',
                                              }}
                                            >
                                              <DeleteIcon fontSize="small" />
                                            </IconButton>
                                          </motion.div>
                                        </Tooltip>
                                      </Box>
                                    </Box>
                                  </Paper>
                                </motion.div>
                              ))}
                            </AnimatePresence>
                          </Stack>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </MotionCard>
              </Grid>
            );
          })}
        </AnimatePresence>
      </Grid>

      {filteredCompanies.length === 0 && (
        <MotionPaper
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          sx={{ 
            textAlign: 'center', 
            py: 10,
            mt: 4,
            background: 'linear-gradient(135deg, #f8f9ff 0%, #fef5ff 100%)',
            borderRadius: 5,
            border: '3px dashed rgba(99, 102, 241, 0.25)',
          }}
        >
          <motion.div
            animate={{ 
              scale: [1, 1.1, 1],
              rotate: [0, 5, -5, 0]
            }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            <BusinessIcon sx={{ fontSize: 100, color: 'rgba(99, 102, 241, 0.25)', mb: 3 }} />
          </motion.div>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#6366f1', mb: 1 }}>
            No companies found
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
            Start your journey by creating your first company and unlock the power of organized project management
          </Typography>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <ActionButton
              startIcon={<AddIcon />}
              onClick={() => handleOpenCompanyDialog()}
              sx={{
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                color: 'white',
                px: 5,
                py: 2,
                borderRadius: 3,
                fontWeight: 800,
                fontSize: '1.2rem',
                boxShadow: '0 8px 32px rgba(99, 102, 241, 0.3)',
                '&:hover': {
                  boxShadow: '0 12px 48px rgba(99, 102, 241, 0.45)',
                  transform: 'translateY(-4px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Create Your First Company
            </ActionButton>
          </motion.div>
        </MotionPaper>
      )}

      {/* Company Dialog */}
      <Dialog 
        open={openCompanyDialog} 
        onClose={handleCloseCompanyDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ 
          p: 0,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        }}>
          <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <BusinessIcon sx={{ fontSize: 32 }} />
              <Box>
                <Typography variant="h5" fontWeight={800}>
                  {editingCompany ? 'Edit Company' : 'Create New Company'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {editingCompany ? 'Update company information' : 'Add a new company to your portfolio'}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleCloseCompanyDialog} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 4, pb: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company Name"
                value={companyFormData.company_name}
                onChange={(e) => setCompanyFormData({ ...companyFormData, company_name: e.target.value })}
                required
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#667eea',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#667eea',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company Code"
                value={companyFormData.company_code}
                onChange={(e) => setCompanyFormData({ ...companyFormData, company_code: e.target.value })}
                required
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#667eea',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#667eea',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Industry"
                value={companyFormData.industry}
                onChange={(e) => setCompanyFormData({ ...companyFormData, industry: e.target.value })}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#667eea',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#667eea',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Website"
                value={companyFormData.website}
                onChange={(e) => setCompanyFormData({ ...companyFormData, website: e.target.value })}
                type="url"
                placeholder="https://example.com"
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#667eea',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#667eea',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Status"
                value={companyFormData.status}
                onChange={(e) => setCompanyFormData({ ...companyFormData, status: parseInt(e.target.value) })}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#667eea',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#667eea',
                  },
                }}
              >
                <MenuItem value={1}>Active</MenuItem>
                <MenuItem value={0}>Inactive</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Logo URL"
                value={companyFormData.logo_url}
                onChange={(e) => setCompanyFormData({ ...companyFormData, logo_url: e.target.value })}
                type="url"
                placeholder="https://example.com/logo.png"
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#667eea',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#667eea',
                  },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <ActionButton 
            variant="outlined" 
            onClick={handleCloseCompanyDialog}
            sx={{
              borderColor: '#667eea',
              color: '#667eea',
              borderRadius: 2,
              px: 3,
              fontWeight: 700,
              '&:hover': {
                borderColor: '#764ba2',
                backgroundColor: '#667eea10',
              },
            }}
          >
            Cancel
          </ActionButton>
          <ActionButton 
            variant="contained" 
            onClick={handleSubmitCompany}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: 2,
              px: 4,
              fontWeight: 700,
              boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
              '&:hover': {
                boxShadow: '0 6px 30px rgba(102, 126, 234, 0.4)',
              },
            }}
          >
            {editingCompany ? 'Update Company' : 'Create Company'}
          </ActionButton>
        </DialogActions>
      </Dialog>

      {/* Project Dialog */}
      <Dialog 
        open={openProjectDialog} 
        onClose={handleCloseProjectDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ 
          p: 0,
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: 'white',
        }}>
          <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ProjectIcon sx={{ fontSize: 32 }} />
              <Box>
                <Typography variant="h5" fontWeight={800}>
                  {editingProject ? 'Edit Project' : 'Create New Project'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {selectedCompany && `for ${selectedCompany.company_name}`}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleCloseProjectDialog} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 4, pb: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Project Name"
                value={projectFormData.project_name}
                onChange={(e) => setProjectFormData({ ...projectFormData, project_name: e.target.value })}
                required
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#f093fb',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#f093fb',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#f093fb',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Project Code"
                value={projectFormData.project_code}
                onChange={(e) => setProjectFormData({ ...projectFormData, project_code: e.target.value })}
                required
                disabled={!!editingProject}
                variant="outlined"
                helperText={editingProject ? "Project code cannot be changed" : ""}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#f093fb',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#f093fb',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#f093fb',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Project Type"
                value={projectFormData.project_type}
                onChange={(e) => setProjectFormData({ ...projectFormData, project_type: e.target.value })}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#f093fb',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#f093fb',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#f093fb',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={projectFormData.location}
                onChange={(e) => setProjectFormData({ ...projectFormData, location: e.target.value })}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#f093fb',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#f093fb',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#f093fb',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={projectFormData.description}
                onChange={(e) => setProjectFormData({ ...projectFormData, description: e.target.value })}
                multiline
                rows={3}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#f093fb',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#f093fb',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#f093fb',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={projectFormData.start_date}
                onChange={(e) => setProjectFormData({ ...projectFormData, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#f093fb',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#f093fb',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#f093fb',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={projectFormData.end_date}
                onChange={(e) => setProjectFormData({ ...projectFormData, end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#f093fb',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#f093fb',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#f093fb',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Status"
                value={projectFormData.status}
                onChange={(e) => setProjectFormData({ ...projectFormData, status: parseInt(e.target.value) })}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#f093fb',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#f093fb',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#f093fb',
                  },
                }}
              >
                <MenuItem value={1}>Active</MenuItem>
                <MenuItem value={0}>Inactive</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <ActionButton 
            variant="outlined" 
            onClick={handleCloseProjectDialog}
            sx={{
              borderColor: '#f093fb',
              color: '#f093fb',
              borderRadius: 2,
              px: 3,
              fontWeight: 700,
              '&:hover': {
                borderColor: '#f5576c',
                backgroundColor: '#f093fb10',
              },
            }}
          >
            Cancel
          </ActionButton>
          <ActionButton 
            variant="contained" 
            onClick={handleSubmitProject}
            sx={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              borderRadius: 2,
              px: 4,
              fontWeight: 700,
              boxShadow: '0 4px 20px rgba(240, 147, 251, 0.3)',
              '&:hover': {
                boxShadow: '0 6px 30px rgba(240, 147, 251, 0.4)',
              },
            }}
          >
            {editingProject ? 'Update Project' : 'Create Project'}
          </ActionButton>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, type: '', id: null })}
        onConfirm={confirmDialog.type === 'company' ? handleDeleteCompany : handleDeleteProject}
        title={`Delete ${confirmDialog.type === 'company' ? 'Company' : 'Project'}`}
        message={`Are you sure you want to delete this ${confirmDialog.type}? This action cannot be undone and will permanently remove all associated data.`}
        confirmText="Delete"
        type="danger"
      />
    </Box>
  );
}

export default Companies;
